import * as React from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'
import { Flex, Heading, Text } from '@buffer-mono/popcorn'
import type { HeadingProps } from '@buffer-mono/popcorn'

import styles from './SettingsRow.module.css'

/**
 * What a row hands to a `Header` or `Control` function child: the ids that
 * tie the control to the copy, and the control size the row's place in the
 * hierarchy calls for. A control sets `aria-labelledby` and
 * `aria-describedby` from the ids; a title that is not a `Heading` takes the
 * heading id.
 */
type SettingsRowRenderProps = {
  headingId: string
  descriptionId: string
  /**
   * `small` inside `SettingsRow.Nested`, `medium` otherwise. Pass it to the
   * control's `size` so a nested row reads as subordinate to its parent.
   */
  controlSize: 'small' | 'medium'
}

const SettingsRowContext = React.createContext<SettingsRowRenderProps | null>(
  null,
)

const SettingsRowNestedContext = React.createContext(false)

function useSettingsRow(): SettingsRowRenderProps {
  const row = React.useContext(SettingsRowContext)

  if (!row) {
    throw new Error('SettingsRow parts must be rendered inside SettingsRow')
  }

  return row
}

type SettingsRowChildren =
  | React.ReactNode
  | ((row: SettingsRowRenderProps) => React.ReactNode)

const renderChildren = (
  children: SettingsRowChildren,
  row: SettingsRowRenderProps,
): React.ReactNode =>
  typeof children === 'function' ? children(row) : children

type SettingsRowProps = {
  children: React.ReactNode
  /**
   * Base for the ids the row assigns to its title and description:
   * `<id>-heading` and `<id>-description`. Defaults to a generated id. Pass
   * one when an element outside the row, or a test, needs a stable id.
   */
  id?: string
  className?: string
}

/**
 * One setting: copy on the left, its control on the right.
 *
 * The row assigns the ids that tie the control to the copy, and owns the
 * responsive rule: an `inline` control stays beside the copy at every width,
 * any other control moves below the copy under the md breakpoint. Inside
 * `SettingsRow.Nested` the row takes the subordinate look: an `h3` title and
 * a `small` control size.
 */
const SettingsRow = ({
  children,
  id,
  className,
}: SettingsRowProps): JSX.Element => {
  const generatedId = React.useId()
  const nested = React.useContext(SettingsRowNestedContext)
  const baseId = id ?? generatedId
  const row = React.useMemo<SettingsRowRenderProps>(
    () => ({
      headingId: `${baseId}-heading`,
      descriptionId: `${baseId}-description`,
      controlSize: nested ? 'small' : 'medium',
    }),
    [baseId, nested],
  )

  return (
    <SettingsRowContext.Provider value={row}>
      <div className={clsx(styles.row, className)}>{children}</div>
    </SettingsRowContext.Provider>
  )
}

type SettingsRowNestedProps = {
  /**
   * The rows a parent row's control governs.
   */
  children: React.ReactNode
  className?: string
}

/**
 * Rows that a parent row's control governs, such as the Beta feature rows
 * under "Enable Beta Features". Each row inside renders its title as `h3` at
 * `xs` and hands its control the `small` size, so the parent control reads
 * as the one in charge. The rows stack with the `xl` gap.
 */
const SettingsRowNested = ({
  children,
  className,
}: SettingsRowNestedProps): JSX.Element => (
  <SettingsRowNestedContext.Provider value={true}>
    <Flex direction="column" gap="xl" fullWidth className={className}>
      {children}
    </Flex>
  </SettingsRowNestedContext.Provider>
)

type SettingsRowHeaderProps = {
  /**
   * The copy block: usually `SettingsRow.Title` and `SettingsRow.Description`.
   * Pass a function to receive the row's render props when the title or the
   * description is another element, such as a `Markdown` block.
   */
  children: SettingsRowChildren
}

const SettingsRowHeader = ({
  children,
}: SettingsRowHeaderProps): JSX.Element => {
  const row = useSettingsRow()

  return (
    <Flex
      direction="column"
      gap="xs"
      align="start"
      justify="center"
      className={styles.copy}
    >
      {renderChildren(children, row)}
    </Flex>
  )
}

type SettingsRowTitleProps = {
  /**
   * Heading level. Pick the level that matches the row's place in the page
   * outline: `h2` for a page-level setting, `h3` for a setting inside a
   * section.
   * @default 'h2', or 'h3' inside `SettingsRow.Nested`
   */
  as?: HeadingProps['as']
  /**
   * Visual size. Use `sm` with `h2` and `xs` with `h3`.
   * @default 'sm', or 'xs' inside `SettingsRow.Nested`
   */
  size?: HeadingProps['size']
  icon?: HeadingProps['icon']
  className?: string
  children: React.ReactNode
}

const SettingsRowTitle = ({
  as,
  size,
  icon,
  className,
  children,
}: SettingsRowTitleProps): JSX.Element => {
  const { headingId } = useSettingsRow()
  const nested = React.useContext(SettingsRowNestedContext)

  return (
    <Heading
      as={as ?? (nested ? 'h3' : 'h2')}
      size={size ?? (nested ? 'xs' : 'sm')}
      id={headingId}
      icon={icon}
      className={className}
    >
      {children}
    </Heading>
  )
}

type SettingsRowDescriptionProps = {
  /**
   * Supporting copy under the title: one or two sentences.
   */
  children: React.ReactNode
  className?: string
}

const SettingsRowDescription = ({
  children,
  className,
}: SettingsRowDescriptionProps): JSX.Element => {
  const { descriptionId } = useSettingsRow()

  return (
    <Text
      as="p"
      id={descriptionId}
      color="subtle"
      className={clsx(styles.description, className)}
    >
      {children}
    </Text>
  )
}

type SettingsRowControlProps = {
  /**
   * The control: a Switch, a Select, a Button, or one group of them. Pass a
   * function to receive the row's render props: the ids for
   * `aria-labelledby` and `aria-describedby`, and the `controlSize`.
   */
  children: SettingsRowChildren
  /**
   * Keep the control beside the copy at every width. Use it for a Switch or
   * a Select, which read as the value of the row. Without it the control
   * moves below the copy under the md breakpoint, which suits a Button.
   */
  inline?: boolean
}

const SettingsRowControl = ({
  children,
  inline = false,
}: SettingsRowControlProps): JSX.Element => {
  const row = useSettingsRow()

  return (
    <Flex
      align="center"
      gap="xs"
      className={inline ? styles.controlInline : styles.control}
    >
      {renderChildren(children, row)}
    </Flex>
  )
}

SettingsRow.displayName = 'SettingsRow'
SettingsRowNested.displayName = 'SettingsRow.Nested'
SettingsRowHeader.displayName = 'SettingsRow.Header'
SettingsRowTitle.displayName = 'SettingsRow.Title'
SettingsRowDescription.displayName = 'SettingsRow.Description'
SettingsRowControl.displayName = 'SettingsRow.Control'

const SettingsRowObject = Object.assign(SettingsRow, {
  Nested: SettingsRowNested,
  Header: SettingsRowHeader,
  Title: SettingsRowTitle,
  Description: SettingsRowDescription,
  Control: SettingsRowControl,
})

export { SettingsRowObject as SettingsRow }
export type {
  SettingsRowRenderProps,
  SettingsRowProps,
  SettingsRowNestedProps,
  SettingsRowHeaderProps,
  SettingsRowTitleProps,
  SettingsRowDescriptionProps,
  SettingsRowControlProps,
}
